import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "ui/Modal";
import EntityCompactCard from "components/Entities/EntityCompactCard";
import PostCreateButton from "ui/PostCreateButton";
import AlignmentModal from "components/Alignment/AlignmentModal";
import AlignmentStanceButton from "components/Alignment/AlignmentStanceButton";
import AiSuggestionPanel from "components/AI/AiSuggestionPanel";
import { Impact, Comment, Issue } from "util/constants";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";
import type { QuizTeabateSelection } from "types/quiz";
import { useDimensionedTeabatesInfiniteQuery } from "hooks/queries/useTeabatePostQueries";

interface UserPerspectiveFormProps {
    isOpen: boolean;
    onClose: () => void;
    issueId?: number;
    selectedIssue?: Issue | null;
    title?: string;
    description?: string;
    issueSelector?: React.ReactNode;
    renderInline?: boolean;
    showCloseAction?: boolean;
    requireIssueForCreation?: boolean;
    requireAlignmentDimension?: boolean;
    dimensionRequiredMessage?: string;
    showTeabateTitle?: boolean;
    selectedTeabates?: QuizTeabateSelection[];
    maxSelections?: number;
    onTeabateRespond?: (payload: { teabateId: number; issueId: number; response: number; importance: number }) => void | Promise<void>;
    copy?: Partial<{
        createButtonText: string;
        createButtonTooltip: string;
        closeLabel: string;
        emptyIssueTeabates: string;
        loadingText: string;
        agreeLabel: string;
        disagreeLabel: string;
        selectIssueFirstText: string;
        selectedTeabatesTitle: string;
        needAtLeastOneTeabate: string;
        maxTeabatesReached: string;
    }>;
}

const DEFAULT_COPY = {
    createButtonText: "Start a Teabate",
    createButtonTooltip: "Create a new teabate",
    closeLabel: "Close",
    emptyIssueTeabates: "No dimensioned teabates yet for this issue.",
    loadingText: "Loading...",
    agreeLabel: "Agree",
    disagreeLabel: "Disagree",
    selectIssueFirstText: "Choose one of your ranked issues first.",
    selectedTeabatesTitle: "Your quiz responses",
    needAtLeastOneTeabate: "Choose at least one teabate before continuing.",
    maxTeabatesReached: "You can save up to five teabates for this quiz.",
} as const;

const UserPerspectiveForm: React.FC<UserPerspectiveFormProps> = ({ 
    isOpen, 
    onClose,
    issueId,
    selectedIssue,
    title = "Share your perspective",
    description = "Respond whether you agree or disagree with a Teabate claim. Your responses help others understand your views.",
    issueSelector = null,
    renderInline = false,
    showCloseAction = true,
    requireIssueForCreation = false,
    requireAlignmentDimension = false,
    dimensionRequiredMessage,
    showTeabateTitle = true,
    selectedTeabates = [],
    maxSelections = 5,
    onTeabateRespond,
    copy = {},
}) => {
    const [selectedTeabate, setSelectedTeabate] = useState<Impact | null>(null);
    const [selectedStance, setSelectedStance] = useState<"agree" | "disagree">("agree");
    const [alignmentEditMode, setAlignmentEditMode] = useState(false);
    const [suggestedClaim, setSuggestedClaim] = useState("");
    const resolvedCopy = useMemo(() => ({ ...DEFAULT_COPY, ...copy }), [copy]);
    const selectedPostIds = useMemo(
        () => new Set(selectedTeabates.map((teabate) => Number(teabate.post_id)).filter((value) => Number.isInteger(value) && value > 0)),
        [selectedTeabates]
    );

    const teabatesQuery = useDimensionedTeabatesInfiniteQuery(issueId ?? null, true, 20);
    const teabateItems = useMemo(
        () => teabatesQuery.data?.pages.flatMap((page) => page.items) ?? [],
        [teabatesQuery.data]
    );
    const {
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = teabatesQuery;
    const observer = useRef<IntersectionObserver | null>(null);

    const lastTeabateElementRef = useCallback((node: HTMLDivElement | null) => {
        if (isFetchingNextPage) {
            return;
        }

        if (observer.current) {
            observer.current.disconnect();
        }

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasNextPage) {
                void fetchNextPage();
            }
        }, {
            root: null,
            rootMargin: "0px",
            threshold: 0.1,
        });

        if (node) {
            observer.current.observe(node);
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    const initialIssue = useMemo<Issue | null>(() => {
        if (selectedIssue) {
            return selectedIssue;
        }
        if (!issueId) {
            return null;
        }
        return {
            id: issueId,
            id_short: "",
            name: "",
            cname: "",
            subject_id: 0,
            subject_cname: "",
            wikipedia_link: "",
            topic_id: 0,
            topic_cname: "",
            topic_wikipedia_link: "",
            created_at: "",
            att_hash: "",
            issue_id: issueId,
            scope: "",
            status: "",
            description: "",
            inactive_at: "",
            is_latest: true,
            user_id: 0,
            updated_at: "",
            upvotes: 0,
            downvotes: 0,
            vote_count: 0,
            user_display_name: "",
            username: "",
            reference_link: "",
            user_vote: "",
            rel_type: "",
            properties: {},
            properties_public: {},
            attributes: null,
        };
    }, [issueId, selectedIssue]);

    const openAlignmentForTeabate = useCallback(async (teabateId: number, stance: "agree" | "disagree") => {
        try {
            const [teabateResponse, alignmentResponse] = await Promise.all([
                api.get(apiRoutes.posts.byId(teabateId)),
                api.get(apiRoutes.user.alignmentByTeabate(teabateId)).catch(() => ({ data: null })),
            ]);
            const resolvedTeabate = teabateResponse.data as Impact;
            const alreadySelected = selectedPostIds.has(Number(resolvedTeabate.id));
            if (!alreadySelected && selectedTeabates.length >= maxSelections) {
                toast.error(resolvedCopy.maxTeabatesReached);
                return;
            }
            setSelectedTeabate(resolvedTeabate);
            setSelectedStance(stance);
            setAlignmentEditMode(Boolean(alignmentResponse.data));
        } catch (error) {
            console.error("Error loading teabate for perspective response:", error);
            return;
        }
    }, [maxSelections, resolvedCopy.maxTeabatesReached, selectedPostIds, selectedTeabates.length]);

    const handleTeabateAdded = useCallback((item: Impact | Comment) => {
        if (!("id" in item)) {
            return;
        }
        const createdTeabate = item as Impact;
        const stance = createdTeabate.properties?.argument_type === "disagree" ? "disagree" : "agree";
        setSelectedTeabate(createdTeabate);
        setSelectedStance(stance);
        setAlignmentEditMode(false);
    }, []);

    useEffect(() => {
        setSuggestedClaim("");
    }, [issueId]);

    const canCreateTeabate = (!requireIssueForCreation || Boolean(issueId && initialIssue)) && selectedTeabates.length < maxSelections;

    const createButton = canCreateTeabate ? (
        <PostCreateButton
            buttonText={resolvedCopy.createButtonText}
            tooltip={resolvedCopy.createButtonTooltip}
            fixedPostType="teabate"
            hidePostTypeButtons={true}
            onImpactAdded={handleTeabateAdded}
            initialIssue={initialIssue}
            teabateNavigateOnSuccess={onTeabateRespond ? false : (issueId ? false : true)}
            teabateRequireAlignmentDimension={requireAlignmentDimension}
            teabateDimensionRequiredMessage={dimensionRequiredMessage}
            teabateInitialClaim={suggestedClaim || undefined}
        />
    ) : (
        <button
            type="button"
            disabled={true}
            className="btn-primary-disabled inline-flex min-h-[44px] items-center justify-center gap-2 px-4 py-2 opacity-60 cursor-not-allowed"
        >
            {resolvedCopy.createButtonText}
        </button>
    );

    const footer = (
        <div className="flex justify-between py-4 border-t border-eoe-border-main dark:border-eoe-dark-border-main">
                
        <div className="">
            {createButton}
        </div>
            {showCloseAction ? (
                <button
                    onClick={onClose}
                    className="btn-secondary px-6"
                    disabled={teabatesQuery.isLoading || teabatesQuery.isFetchingNextPage}
                >
                    {teabatesQuery.isLoading || teabatesQuery.isFetchingNextPage ? <Loader2 className="size-4 animate-spin" /> : resolvedCopy.closeLabel}
                </button>
            ) : null}
        </div>
    );

    const bodyContent = (
        <div className="py-1">
            {!renderInline ? (
                <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub mb-4">
                    {description}
                </p>
            ) : null}

            {issueSelector ? (
                <div className="mb-4">
                    {issueSelector}
                </div>
            ) : null}

            {selectedTeabates.length > 0 ? (
                <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                            {resolvedCopy.selectedTeabatesTitle}
                        </p>
                        <span className="rounded-full bg-eoe-blue/10 px-2 py-1 text-xs font-semibold text-eoe-blue dark:bg-eoe-blue/20">
                            {selectedTeabates.length}/{maxSelections}
                        </span>
                    </div>
                    <div className="space-y-2">
                        {selectedTeabates.map((teabate) => (
                            <button
                                key={String(teabate.id ?? teabate.post_id ?? teabate.post.title ?? "selected-teabate")}
                                type="button"
                                onClick={() => {
                                    if (teabate.post_id) {
                                        void openAlignmentForTeabate(teabate.post_id, "agree");
                                    }
                                }}
                                className="w-full rounded-2xl border border-eoe-border-main bg-eoe-bg-hover p-3 text-left dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover"
                            >
                                {showTeabateTitle && teabate.post.title ? (
                                    <h3 className="mb-2 text-base font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {teabate.post.title}
                                    </h3>
                                ) : null}
                                <EntityCompactCard
                                    entityId={teabate.post_id ?? undefined}
                                    entityType="teabate"
                                    showActions={false}
                                    showMeta={false}
                                    compactCard={true}
                                    showVote={false}
                                    showLink={false}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            {issueId ? (
                <div className="mb-4">
                    <AiSuggestionPanel
                        issueId={issueId}
                        excludedTeabateIds={Array.from(selectedPostIds)}
                        onUseSuggestion={setSuggestedClaim}
                    />
                </div>
            ) : null}

            {issueId || !requireIssueForCreation ? (
                <div className="min-h-[320px] space-y-3">
                    {teabatesQuery.isLoading ? (
                        <div className="flex items-center justify-center py-8 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            <Loader2 className="mr-2 size-4 animate-spin" />
                            {resolvedCopy.loadingText}
                        </div>
                    ) : teabateItems.length > 0 ? (
                        <>
                        {teabateItems.map((teabate, index) => {
                            return (
                                <div
                                    key={String(teabate.id ?? teabate.title ?? "teabate")}
                                    ref={index === teabateItems.length - 1 ? lastTeabateElementRef : null}
                                    className="rounded-2xl border border-eoe-border-main bg-eoe-bg-hover p-3 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover"
                                >
                                    {showTeabateTitle && teabate.title ? (
                                        <h3 className="mb-2 text-base font-semibold text-eoe-text dark:text-eoe-dark-text">
                                            {teabate.title}
                                        </h3>
                                    ) : null}
                                    <EntityCompactCard
                                        entity={teabate as unknown as Impact}
                                        entityId={teabate.id ?? undefined}
                                        entityType="teabate"
                                        showActions={false}
                                        showMeta={false}
                                        compactCard={true}
                                        showVote={false}
                                        showLink={false}
                                    />
                                    <div className="mt-2 flex items-center gap-2">
                                        <AlignmentStanceButton
                                            variant="agree"
                                            showCount={false}
                                            showText={true}
                                            textOverride={resolvedCopy.agreeLabel}
                                            onClick={() => {
                                                if (teabate.id) {
                                                    void openAlignmentForTeabate(teabate.id, "agree");
                                                }
                                            }}
                                        />
                                        <AlignmentStanceButton
                                            variant="disagree"
                                            showCount={false}
                                            showText={true}
                                            textOverride={resolvedCopy.disagreeLabel}
                                            onClick={() => {
                                                if (teabate.id) {
                                                    void openAlignmentForTeabate(teabate.id, "disagree");
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                        {teabatesQuery.isFetchingNextPage ? (
                            <div className="flex items-center justify-center py-4 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                <Loader2 className="mr-2 size-4 animate-spin" />
                                {resolvedCopy.loadingText}
                            </div>
                        ) : null}
                        </>
                    ) : (
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {resolvedCopy.emptyIssueTeabates}
                        </p>
                    )}
                </div>
            ) : renderInline && requireIssueForCreation ? (
                <div className="rounded-2xl border border-dashed border-eoe-border-main bg-eoe-bg-hover px-4 py-6 text-sm text-eoe-text-sub dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text-sub">
                    {resolvedCopy.selectIssueFirstText}
                </div>
            ) : null}
        </div>
    );

    if (renderInline) {
        return (
            <>
                <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8">
                    <div className="space-y-2">
                        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
                            {title}
                        </h1>
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {description}
                        </p>
                    </div>
                    <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
                        {bodyContent}
                        <div className="mt-4">
                            {footer}
                        </div>
                    </section>
                </div>
                {selectedTeabate ? (
                    <AlignmentModal
                        isOpen={Boolean(selectedTeabate)}
                        onClose={() => setSelectedTeabate(null)}
                        teabate={selectedTeabate}
                        stance={selectedStance}
                        editMode={alignmentEditMode}
                        onSaved={(payload) => {
                            if (!selectedTeabate) {
                                return;
                            }
                            onTeabateRespond?.({
                                teabateId: selectedTeabate.id,
                                issueId: Number(selectedTeabate.issue_id),
                                response: payload.response,
                                importance: payload.importance,
                            })
                        }}
                    />
                ) : null}
            </>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            footer={footer}
            stickyFooter={true}
            className="max-w-4xl"
            disableOutsideClose={true}
        >
            {bodyContent}
            {selectedTeabate ? (
                <AlignmentModal
                    isOpen={Boolean(selectedTeabate)}
                    onClose={() => setSelectedTeabate(null)}
                    teabate={selectedTeabate}
                    stance={selectedStance}
                    editMode={alignmentEditMode}
                    onSaved={(payload) => {
                        if (!selectedTeabate) {
                            return;
                        }
                        onTeabateRespond?.({
                            teabateId: selectedTeabate.id,
                            issueId: Number(selectedTeabate.issue_id),
                            response: payload.response,
                            importance: payload.importance,
                        })
                    }}
                />
            ) : null}
        </Modal>
    );
};

export default UserPerspectiveForm;